import { Formik, Field, FormikHelpers } from 'formik';
import { DateTime } from 'luxon';
import i18n from 'i18next';
import * as Yup from 'yup';

import {
  Modal,
  Button,
  Select,
  TextField,
  DatePickerField,
} from 'web-components';

import { Flex } from 'web-styled';

import * as S from './styled';

// Types
interface ClientOption {
  value: string;
  label: string;
}

interface ActivityFormValues {
  person_id: string | number;
  begins_at: DateTime;
  ends_at: DateTime;
  workout_name: string;
  description: string;
  id?: number;
}

interface Props {
  isOpen: boolean;
  clients: ClientOption[];
  startDate: Date;
  initialClient: number;
  initialValues?: ActivityFormValues;
  onSubmit: (values: ActivityFormValues, actions: FormikHelpers<ActivityFormValues>) => void | Promise<void>;
  onRequestClose: () => void;
}

// Constants
const selectProps = {
  styles: {
    container: (provided: any) => ({
      ...provided,
      width: '100%',
    }),
    menu: (provided: any) => ({
      ...provided,
      marginTop: '1px',
    }),
  },
  theme: (theme: any) => ({
    ...theme,
    borderRadius: 3,
  }),
};

const validationSchema = Yup.object().shape({
  person_id: Yup.string()
    .required('Client is required'),
  workout_name: Yup.string()
    .required('Name is required'),
  // description: Yup.string()
  //   .required('Description is required'),
});

// Component
const NewActivityLogger = ({
  isOpen,
  startDate,
  clients,
  initialClient,
  initialValues,
  onSubmit,
  onRequestClose,
}: Props) => {
  const defaultInitialValues: ActivityFormValues = {
    person_id: initialClient,
    begins_at: DateTime.fromJSDate(startDate),
    ends_at: DateTime.fromJSDate(startDate).plus({ hours: 1 }),
    workout_name: '',
    description: '',
  };

  const handleDateChange = (
    date: Date,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const start = DateTime.fromJSDate(date);
    setFieldValue('begins_at', start);
    setFieldValue('ends_at', start.plus({ hours: 1 }));
  };

  const handleEndDateChange = (
    date: Date,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue('ends_at', DateTime.fromJSDate(date));
  };

  const handleClientChange = (
    option: ClientOption | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (option) {
      setFieldValue('person_id', option.value);
    }
  };

  return (
    <Modal
      title="Activity Logger"
      isOpen={isOpen}
      contentStyle={{ width: 400 }}
      onRequestClose={onRequestClose}
    >
      <Formik<ActivityFormValues>
        initialValues={initialValues || defaultInitialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {(formikProps) => {
          const selectedClient = clients.find(
            (option) => option.value === String(formikProps.values.person_id)
          );

          return (
            <Flex
              as="form"
              column
              width="100%"
              onSubmit={formikProps.handleSubmit}
            >
              {initialValues?.id === undefined && (
                <Flex width="100%" column>
                  <S.Label htmlFor="person_id-select">
                    {i18n.t('client')}
                  </S.Label>
                  <Select
                    {...selectProps}
                    id="person_id-select"
                    value={selectedClient || null}
                    name="person_id"
                    options={clients}
                    onChange={(option: any) =>
                      handleClientChange(option, formikProps.setFieldValue)
                    }
                  />
                </Flex>
              )}

              <Flex width="100%" spacing={10}>
                <Flex column>
                  <S.Label>Start</S.Label>
                  <Field
                    name="begins_at"
                    dateFormat="MM/dd/yyyy   hh:mm a"
                    showTimeSelect
                    minDate={new Date()}
                    onChange={(date: Date) =>
                      handleDateChange(date, formikProps.setFieldValue)
                    }
                    component={DatePickerField}
                  />
                </Flex>

                <Flex column>
                  <S.Label>End</S.Label>
                  <Field
                    name="ends_at"
                    selected={new Date(formikProps.values.begins_at.toISO())}
                    dateFormat="MM/dd/yyyy   hh:mm a"
                    showTimeSelect
                    minDate={new Date(formikProps.values.begins_at.toISO())}
                    onChange={(date: Date) =>
                      handleEndDateChange(date, formikProps.setFieldValue)
                    }
                    component={DatePickerField}
                  />
                </Flex>
              </Flex>

              <Flex width="100%" column>
                <S.Label>Activity Name</S.Label>
                <Field
                  name="workout_name"
                  component={TextField}
                  placeholder="Enter name"
                />
              </Flex>

              <Flex width="100%" column>
                <S.Label>Description</S.Label>
                <Field
                  name="description"
                  textarea
                  minRows={4}
                  placeholder="Enter a description (optional)"
                  component={TextField}
                />
              </Flex>

              <S.Footer>
                <hr />
                <Flex width="100%" justify="flex-end" spacing={20}>
                  <Button ghost appearance="danger" onClick={onRequestClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    appearance="primary"
                    isLoading={formikProps.isSubmitting}
                    isDisabled={formikProps.isSubmitting}
                  >
                    Save
                  </Button>
                </Flex>
              </S.Footer>
            </Flex>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NewActivityLogger;
